import {useRef, useState} from "react";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import gsap from "gsap";
import cn from "classnames";
import Select from "react-select";

/**
 * Paleta de react-select, corregida por contraste. Los defaults no llegan a los
 * mínimos de WCAG en ningún fondo del sitio:
 *   - borde y flecha (`neutral20`, #cccccc)  1.61:1 sobre blanco y 1.44:1 sobre
 *     la banda celeste del cotizador; 1.4.11 pide 3:1 para el límite de un control.
 *   - placeholder (`neutral50`, #808080)     3.95:1 sobre blanco; 1.4.3 pide 4.5:1.
 *   - opción elegida (blanco sobre `primary`, #2684FF)  3.60:1.
 *
 * Se reemplazan sólo esos tokens, con colores que ya usa la marca, así todos
 * los selects del sitio siguen siendo idénticos entre sí. Medidos contra
 * blanco y contra #e6f6f6: borde 3.98 / 3.58, hover 7.58 / 6.82,
 * placeholder 5.75 / 5.17, opción elegida 11.20.
 * @param {Object} base Tema que pasa react-select.
 * @returns {Object}
 */
const accessibleTheme = (base) => ({
    ...base,
    colors: {
        ...base.colors,
        neutral20: '#158e8c',
        neutral30: '#0b5e5c',
        neutral40: '#0b5e5c',
        neutral50: '#666665',
        neutral60: '#064246',
        primary: '#064246',
        primary75: '#158e8c',
        primary50: '#cdeeed',
        primary25: '#e6f6f6',
    },
});

export const SelectBox = ({title, name, onChange, validation = false, options, value, ...rest}) => {
    const opts = options.map(x => ({
        value: x.value,
        label: x.text
    }));

    // `rest` pasa derecho a react-select: lo usa el cotizador para `inputId`
    // (foco en el primer campo inválido) y `aria-*`. Sin props extra se
    // comporta igual que antes.
    return (
      <Select
        instanceId={name}
        placeholder={title}
        options={opts}
        value={opts.find((opt) => opt.value === value) || null}
        onChange={onChange}
        noOptionsMessage={() => 'Sin opciones disponibles'}
        theme={accessibleTheme}
        {...rest}
      />
    );
}

export const SelectBox2 = ({title, name, onChange, validation = false, options}) => {
    const [currentValue, setCurrentValue] = useState('');
    const [isOpen, setIsOpen] = useState(true);
    const dropdownContent = useRef(null);
    const dropdownArrow = useRef(null);

    const ref = useRef();
    useOnClickOutside(ref, () => !isOpen && toggleDropdown());

    const toggleDropdown = () => {
        setIsOpen(!isOpen);

        gsap.to(dropdownContent.current, {
            height: isOpen ? 'auto' : '0',
            ease: 'expo.out',
            duration: 0.7
        });
        gsap.to(dropdownArrow.current, {
            rotateX: isOpen ? '-180deg' : 0,
            ease: 'expo.out',
            duration: 0.7
        });
    };

    return (
        <div className="w-full" ref={ref}>
            <button
                type="button"
                onClick={toggleDropdown}
                className="relative w-full flex flex-col items-start"
            >
                <div className="w-full input-border-bottom">
                    <label htmlFor={name} className="w-full text-left block">
                        <span className="text-grey-dark">{title}</span>
                    </label>
                    <div className="w-full flex justify-between items-center">
                        <input readOnly value={currentValue.text} className={cn('w-full py-10 flex-1')}/>
                        <div>
                            <svg
                                ref={dropdownArrow}
                                className="w-16 h-auto"
                                viewBox="0 0 16 9"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M1.25 0.75L8 8.25L14.75 0.75" stroke="#064246"/>
                            </svg>
                        </div>
                    </div>
                </div>
                <div
                    className={cn('w-full h-1 absolute bottom-0 left-0 duration-30', {
                        'bg-red': validation && !currentValue
                    })}
                ></div>
            </button>
            <div className="absolute z-10 bg-white">
                <div ref={dropdownContent} className="h-0 overflow-hidden">
                    <div
                        className="border border-solid border-grey h-200 overflow-y-scroll"
                        data-lenis-prevent
                    >
                        {options &&
                            options.map((option) => (
                                <button
                                    type="button"
                                    key={option.value}
                                    className="block w-full text-left p1 text-black opacity-40 py-10 pl-8 cursor-pointer hover:bg-grey"
                                    onClick={() => {
                                        setCurrentValue(option);
                                        onChange({
                                            target: {
                                                name,
                                                value: option.value,
                                                text: option.text
                                            }
                                        });
                                        toggleDropdown();
                                    }}
                                >
                                    {option.text}
                                </button>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
